import {
  Loading,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import i18n from 'i18next'

import { SortButton } from '../../../components/SortButton/SortButton'
import { Filtering, FilteringValue } from '../../../types/filtering'
import {
  OptimizationProcess,
  PipelineType,
  RUNNING,
} from '../../../types/optimizationProcesses'
import { SortingDirection } from '../../../types/sorting'
import { formatDateTime } from '../../../utils/date'
import { hasKpiValue } from '../../../utils/kpi'
import { getOptimizationProcessStatus } from '../../../utils/optimizationProcesses'
import { AbortButton } from './AbortButton'
import { DeltaCell } from './DeltaCell'
import { DownloadPlanningButton } from './DownloadPlanningButton'
import { FilterByStatus } from './FilterByStatus'

type Columns = OptimizationProcess & {
  actions?: string
}

type ColumnsKeys = keyof Columns

type Column = TableColumnInstance<Columns, ColumnsKeys>

type UseColumnsProps = {
  pipelineType: PipelineType
  sorting: { [key in keyof Partial<Columns>]: SortingDirection }
  handleSort: (key: ColumnsKeys, direction: SortingDirection) => void
  filtering: Filtering<Columns>
  handleFilter: (key: ColumnsKeys, value: FilteringValue) => void
}

// Exported for reuse by other date-only columns (e.g. DrugRegistry's
// `lastMachinedAt`) that must not go through `formatDateTime`, which prints
// a meaningless "00:00" time for a backend field that carries no time
// component.
export const formatPlanningDate = (date: Date) =>
  new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date))

export const useColumns = ({
  pipelineType,
  sorting,
  filtering,
  handleSort,
  handleFilter,
}: UseColumnsProps): Column[] => {
  // Columns shared by both starting points.
  const idColumn: Column = {
    id: 'id',
    header: (
      <TableColumnHeader
        title={i18n.t('Id')}
        sorting={
          <SortButton
            sort={sorting?.id}
            onSort={(direction) => handleSort('id', direction)}
          />
        }
      />
    ),
    th: true,
    template: ({ id }) => `#${id}`,
  }

  // Filter only, no sorting: ordering by status groups rows by an opaque enum
  // order that means nothing to the user, and the filter already answers the
  // question ("show me the failed ones") that sorting was standing in for.
  const statusColumn: Column = {
    id: 'status',
    header: (
      <TableColumnHeader
        title={i18n.t('Status')}
        filtering={
          <FilterByStatus
            filter={filtering?.status}
            onFilter={(value) => handleFilter('status', value)}
          />
        }
      />
    ),
    template: ({ status }) => (
      <>
        {getOptimizationProcessStatus(status)}{' '}
        {status === RUNNING && <Loading />}
      </>
    ),
  }

  // No sort button: `scenario` is not in the server `ordering_fields`
  // whitelist, and DRF drops unknown ordering keys without any error, so the
  // button used to light up and change nothing.
  const scenarioColumn: Column = {
    id: 'scenario',
    header: (
      <span data-tip={i18n.t('scenario info')} data-for="info-tooltip">
        <TableColumnHeader title={i18n.t('Scenario')} />
      </span>
    ),
    noWrap: true,
    template: ({ scenario }) =>
      !scenario || scenario === '' ? 'N/A' : scenario,
  }

  const createdAtColumn: Column = {
    id: 'createdAt',
    header: (
      <TableColumnHeader
        title={i18n.t('Created on')}
        sorting={
          <SortButton
            sort={sorting?.createdAt}
            onSort={(direction) => handleSort('createdAt', direction)}
          />
        }
      />
    ),
    template: ({ createdAt }) => formatDateTime(createdAt),
  }

  // One column for what is always read as one fact. No sort button either:
  // `planning_start` is not in the server `ordering_fields` whitelist, so the
  // button was inert — DRF discards unknown ordering keys silently.
  const salesRangeColumn: Column = {
    id: 'planningStart',
    header: <TableColumnHeader title={i18n.t('salesRange')} />,
    noWrap: true,
    template: ({ planningStart, planningEnd }) =>
      `${formatPlanningDate(planningStart)} – ${formatPlanningDate(
        planningEnd
      )}`,
  }

  const actionsColumn: Column = {
    id: 'actions',
    header: i18n.t('Actions/Results'),
    noWrap: true,
    width: 0,
    template: ({ id, status }) => (
      <>
        <AbortButton optimizationProcessId={id} status={status} />
        <DownloadPlanningButton optimizationProcessId={id} status={status} />
      </>
    ),
  }

  // KPI columns specific to the "empty warehouse" (from_empty) starting point.
  const totalNumberRefillmentColumn: Column = {
    id: 'totalNumberRefillment',
    header: (
      <span
        data-tip={i18n.t('totalNumberRefillmentInfo')}
        data-for="info-tooltip"
      >
        <TableColumnHeader
          title={i18n.t('totalNumberRefillment')}
          sorting={
            <SortButton
              sort={sorting?.totalNumberRefillment}
              onSort={(direction) =>
                handleSort('totalNumberRefillment', direction)
              }
            />
          }
        />
      </span>
    ),
    th: true,
    template: ({ totalNumberRefillment }) => (
      <div style={{ textAlign: 'right' }}>
        {hasKpiValue(totalNumberRefillment)
          ? new Intl.NumberFormat('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(totalNumberRefillment)
          : 'N/A'}
      </div>
    ),
  }

  const totalAutomationDegreeColumn: Column = {
    id: 'totalAutomationDegree',
    header: (
      <span
        data-tip={i18n.t('totalAutomationDegreeInfo')}
        data-for="info-tooltip"
      >
        <TableColumnHeader
          title={i18n.t('totalAutomationDegree')}
          sorting={
            <SortButton
              sort={sorting?.totalAutomationDegree}
              onSort={(direction) =>
                handleSort('totalAutomationDegree', direction)
              }
            />
          }
        />
      </span>
    ),
    th: true,
    // N/A, not 0%: a process still queued or running has measured no automation
    // at all, and printing a zero claimed a result it does not have yet.
    template: ({ totalAutomationDegree }) =>
      hasKpiValue(totalAutomationDegree)
        ? `${(totalAutomationDegree * 100).toFixed(2)}%`
        : 'N/A',
  }

  // Columns specific to the "last configuration" (from_last_config) starting
  // point: deltas vs the current warehouse configuration.
  // Sorted on the absolute delta, the figure the cell leads with: the
  // percentages next to it are derived from it against different bases.
  const deltaAutomationColumn: Column = {
    id: 'deltaAutomationAbs',
    header: (
      <span data-tip={i18n.t('deltaAutomationInfo')} data-for="info-tooltip">
        <TableColumnHeader
          title={i18n.t('deltaAutomation')}
          sorting={
            <SortButton
              sort={sorting?.deltaAutomationAbs}
              onSort={(direction) =>
                handleSort('deltaAutomationAbs', direction)
              }
            />
          }
        />
      </span>
    ),
    th: true,
    template: ({
      deltaAutomationAbs,
      deltaAutomationOnTotalPct,
      automationShareBeforePct,
      automationShareAfterPct,
    }) => (
      <DeltaCell
        abs={deltaAutomationAbs}
        pct={deltaAutomationOnTotalPct}
        beforePct={automationShareBeforePct}
        afterPct={automationShareAfterPct}
        goodWhen="positive"
      />
    ),
  }

  const deltaRefillColumn: Column = {
    id: 'deltaRefillAbs',
    header: (
      <span data-tip={i18n.t('deltaRefillInfo')} data-for="info-tooltip">
        <TableColumnHeader
          title={i18n.t('deltaRefill')}
          sorting={
            <SortButton
              sort={sorting?.deltaRefillAbs}
              onSort={(direction) => handleSort('deltaRefillAbs', direction)}
            />
          }
        />
      </span>
    ),
    th: true,
    template: ({ deltaRefillAbs, deltaRefillPct }) => (
      <DeltaCell
        abs={deltaRefillAbs}
        pct={deltaRefillPct}
        goodWhen="negative"
      />
    ),
  }

  const timeSavedColumn: Column = {
    id: 'timeSavedMinutes',
    header: (
      <span data-tip={i18n.t('timeSavedInfo')} data-for="info-tooltip">
        <TableColumnHeader
          title={i18n.t('timeSaved')}
          sorting={
            <SortButton
              sort={sorting?.timeSavedMinutes}
              onSort={(direction) => handleSort('timeSavedMinutes', direction)}
            />
          }
        />
      </span>
    ),
    th: true,
    template: ({ timeSavedMinutes }) => (
      <div style={{ textAlign: 'right' }}>
        {hasKpiValue(timeSavedMinutes)
          ? new Intl.NumberFormat('it-IT', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(timeSavedMinutes)
          : 'N/A'}
      </div>
    ),
  }

  if (pipelineType === 'from_last_config') {
    return [
      idColumn,
      statusColumn,
      scenarioColumn,
      createdAtColumn,
      salesRangeColumn,
      deltaAutomationColumn,
      deltaRefillColumn,
      timeSavedColumn,
      actionsColumn,
    ]
  }

  return [
    idColumn,
    statusColumn,
    scenarioColumn,
    createdAtColumn,
    salesRangeColumn,
    totalAutomationDegreeColumn,
    totalNumberRefillmentColumn,
    actionsColumn,
  ]
}
